import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  Button,
  ButtonGroup,
  InlineStack,
  BlockStack,
  Icon,
  InlineError,
} from "@shopify/polaris";
import axios from "../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import { EditIcon, DeleteIcon, PlusIcon } from "@shopify/polaris-icons";

const ProjectTaskManagerAddEdit = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    name: "",
    status: "1",
    project_id: "",
    categories: [],
    statuses: [{ name: "Backlog", type: "1" }],
  });

  const [errors, setErrors] = useState({});
  const [projects, setProjects] = useState([]);

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [toastActive, setToastActive] = useState(false);
  const toggleToast = useCallback(() => setToastActive((a) => !a), []);
  const toastMarkup = toastActive ? (
    <Toast content="Project saved successfully" onDismiss={toggleToast} />
  ) : null;

  const validateFields = () => {
    const required = ["name", "status"];
    const newErrors = {};
    required.forEach((f) => {
      if (!item[f] || item[f].toString().trim() === "") {
        newErrors[f] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Fetch projects for dropdown
  useEffect(() => {
    axios
      .get("/projects?all")
      .then((res) => {
        const list = res?.data?.data || [];
        setProjects([
          { label: "None", value: "" },
          ...list.map((p) => ({ label: p.name, value: String(p.id) })),
        ]);
      })
      .catch(console.error);

    if (mode === "edit") {
      setIsSaving(true);
      axios
        .get(`/task-manager-projects/${id}`)
        .then((res) => {
          const data = res?.data?.data || {};
          // ✅ Normalize categories
          const normalizedCategories =
            (data.categories || []).map((c) => ({
              id: c.id ? String(c.id) : undefined,
              name: c.title || "",
            })) || [];

          // ✅ Normalize statuses
          const normalizedStatuses =
            (data.statuses || []).map((s) => ({
              id: s.id ? String(s.id) : undefined,
              name: s.name || "",
              type: s.type.id, // keep type as text ("active", "completed")
            })) || [];

          setItem({
            name: data.title || "",
            status: String(data.type?.id || "1"),
            project_id: data.project_id ? String(data.project_id) : "",
            categories: normalizedCategories,
            statuses: normalizedStatuses,
          });
        })
        .finally(() => setIsSaving(false));
    }
  }, []);

  // ✅ Dynamic handlers for categories & statuses
  const addCategory = () =>
    setItem((prev) => ({
      ...prev,
      categories: [...prev.categories, { name: "" }],
    }));

  const updateCategory = (index, value) =>
    setItem((prev) => {
      const updated = [...prev.categories];
      updated[index].name = value;
      return { ...prev, categories: updated };
    });

  const removeCategory = (index) =>
    setItem((prev) => {
      const updated = prev.categories.filter((_, i) => i !== index);
      return { ...prev, categories: updated };
    });

  const addStatus = () =>
    setItem((prev) => ({
      ...prev,
      statuses: [...prev.statuses, { name: "", type: "1" }],
    }));

  const updateStatus = (index, field, value) =>
    setItem((prev) => {
      const updated = [...prev.statuses];
      updated[index][field] = value;
      return { ...prev, statuses: updated };
    });

  const removeStatus = (index) =>
    setItem((prev) => {
      const updated = prev.statuses.filter((_, i) => i !== index);
      return { ...prev, statuses: updated };
    });

  const handleSave = () => {
    console.log(item.statuses);
    if (!validateFields()) return;
    setIsSaving(true);

    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");
    formData.append("title", item.name);
    formData.append("type", item.status);
    formData.append("project_id", item.project_id);

    console.log("statuses=", item.statuses);
    // ✅ Append categories
    item.categories.forEach((cat, i) => {
      if (cat.name.trim()) {
        if (cat.id) formData.append(`categories[${i}][id]`, cat.id);
        formData.append(`categories[${i}][title]`, cat.name);
      }
    });

    // ✅ Append statuses
    item.statuses.forEach((st, i) => {
      if (st.name.trim()) {
        if (st.id) formData.append(`statuses[${i}][id]`, st.id);
        formData.append(`statuses[${i}][name]`, st.name);
        formData.append(`statuses[${i}][type]`, st.type);
      }
    });

    axios
      .post(
        mode === "new"
          ? "/task-manager-projects"
          : `/task-manager-projects/${id}`,
        formData
      )
      .then((res) => {
        // console.log("aaa=", res.data.data.id);
        mode === "new"
          ? navigate(`/admin/task-manager/projects/${res.data.data.id}/tasks`)
          : toggleToast();
      })
      .catch((err) => {
        console.error("errorrr=", err.response.data.message);
        setError(
          err.response.data.message || "Something went wrong, try again."
        );
      })
      .finally(() => setIsSaving(false));
  };

  const [deleteActive, setDeleteActive] = useState(false);
  const toggleDeleteModal = useCallback(() => setDeleteActive((a) => !a), []);

  const handleDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/task-manager-projects/${id}`)
      .then(() => navigate("/admin/task-manager/projects"))
      .finally(() => setIsSaving(false));
  };
  const [error, setError] = useState("");

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Project`}
      backAction={{ onAction: () => navigate("/admin/task-manager/projects") }}
    >
      {isSaving && <Loading />}
      <Card sectioned>
        <FormLayout>
          <TextField
            label="Name"
            value={item.name}
            onChange={(value) => handleChange("name", value)}
            error={errors.name}
          />
          <Select
            label="Status"
            options={[
              { label: "Active", value: "1" },
              { label: "Archived", value: "2" },
            ]}
            value={item.status}
            onChange={(value) => handleChange("status", value)}
            placeholder="Please select"
            error={errors.status}
          />
          <Select
            label="Project"
            options={projects}
            value={item.project_id}
            onChange={(value) => handleChange("project_id", value)}
            placeholder="Please select"
            error={errors.project_id}
          />
          {/* ✅ Categories list */}
          <Card>
            <BlockStack gap="200">
              <Text>Categories</Text>
              {item.categories.map((cat, i) => (
                <InlineStack key={i} align="space-between" blockAlign="center">
                  <TextField
                    label={`Category ${i + 1}`}
                    value={cat.name}
                    onChange={(value) => updateCategory(i, value)}
                  />
                  <Button
                    icon={DeleteIcon}
                    tone="critical"
                    onClick={() => removeCategory(i)}
                  />
                </InlineStack>
              ))}
              <Button icon={PlusIcon} onClick={addCategory}>
                Add Category
              </Button>
            </BlockStack>
          </Card>
          {/* ✅ Statuses list */}
          <Card>
            <BlockStack gap="200">
              <Text>Statuses</Text>
              {item.statuses.map((st, i) => (
                <InlineStack key={i} align="space-between" blockAlign="center">
                  <TextField
                    label={`Status ${i + 1} Name`}
                    value={st.name}
                    onChange={(value) => updateStatus(i, "name", value)}
                    disabled={
                      String(st.type) === "1" &&
                      st.name.toLowerCase() === "backlog"
                    }
                  />
                  <Select
                    label="Type"
                    options={[
                      { label: "Backlog", value: "1" },
                      { label: "In Progress", value: "2" },
                      { label: "Done", value: "3" },
                    ]}
                    value={st.type}
                    onChange={(value) => updateStatus(i, "type", value)}
                    disabled={
                      String(st.type) === "1" &&
                      st.name.toLowerCase() === "backlog"
                    }
                  />
                  <Button
                    icon={DeleteIcon}
                    tone="critical"
                    onClick={() => removeStatus(i)}
                    disabled={
                      String(st.type) === "1" &&
                      st.name.toLowerCase() === "backlog"
                    }
                  />
                </InlineStack>
              ))}
              <Button icon={PlusIcon} onClick={addStatus}>
                Add Status
              </Button>
            </BlockStack>
          </Card>
          <InlineError message={error} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && [
            {
              content: "Delete",
              destructive: true,
              onAction: () => setDeleteActive(true),
              loading: isSaving,
            },
          ]
        }
      />

      <Modal
        open={deleteActive}
        onClose={toggleDeleteModal}
        title="Delete Project"
        primaryAction={{
          content: "Yes",
          destructive: true,
          onAction: handleDelete,
          loading: isSaving,
        }}
        secondaryActions={[{ content: "No", onAction: toggleDeleteModal }]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this project?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
    </Page>
  );
};

export default ProjectTaskManagerAddEdit;
